import React from "react";
import { Box, Dialog, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { makeStyles } from "tss-react/mui";
import { BRAND, FONT, RADIUS } from "../lightTheme";
import { useLightStyles } from "../lightTheme";

const useStyles = makeStyles()(() => ({
  body: { padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" },
  text: { ...FONT.body, color: BRAND.textMain, fontFamily: FONT.family, lineHeight: 1.6 },
  textarea: {
    width: "100%", boxSizing: "border-box", borderRadius: RADIUS.sm,
    border: `1.5px solid ${BRAND.border}`, padding: "0.6rem 0.75rem",
    fontFamily: FONT.family, ...FONT.body, resize: "none", outline: "none",
    color: BRAND.textMain, lineHeight: 1.5, transition: "border-color 0.15s",
    "&:focus": { borderColor: BRAND.primary },
  },
  actions: { display: "flex", gap: "0.75rem", justifyContent: "flex-end", paddingTop: "0.5rem" },
  btnDanger: {
    padding: "0.5rem 1.25rem", borderRadius: RADIUS.md, border: "none",
    backgroundColor: "#DC2626", color: BRAND.white, fontFamily: FONT.family,
    ...FONT.h3, cursor: "pointer", transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
}));

/**
 * Reusable stop/deactivate confirmation dialog for Light mode.
 * @param {boolean} open
 * @param {function} onClose - called when dialog should close
 * @param {function} onConfirm - called when user clicks the danger button
 * @param {boolean} loading - disables buttons and shows loading label
 * @param {string} title - dialog header text
 * @param {React.ReactNode} children - confirmation body content (JSX)
 * @param {string} reasonLabel - label above the textarea
 * @param {string} reasonPlaceholder - textarea placeholder
 * @param {string} reason - current reason value
 * @param {function} onReasonChange - reason setter
 * @param {string} cancelLabel
 * @param {string} confirmLabel - normal state button label
 * @param {string} confirmingLabel - loading state button label
 */
export default function StopConfirmDialog({
  open, onClose, onConfirm, loading,
  title, children,
  reasonLabel, reasonPlaceholder,
  reason, onReasonChange,
  cancelLabel, confirmLabel, confirmingLabel,
}) {
  const { classes: s } = useStyles();
  const { classes: light } = useLightStyles();

  return (
    <Dialog
      open={open}
      onClose={() => { if (!loading) onClose(); }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, overflow: "hidden" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: "#DC2626", pb: 1.5 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <StopCircleOutlinedIcon style={{ fontSize: "18px" }} />
          {title}
        </Box>
        <IconButton size="small" onClick={() => { if (!loading) onClose(); }} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Box className={s.body}>
        <Typography className={s.text}>{children}</Typography>
        {onReasonChange != null && (
          <Box>
            <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.35rem" }}>
              {reasonLabel}
            </Typography>
            <textarea
              className={s.textarea}
              placeholder={reasonPlaceholder}
              value={reason ?? ""}
              onChange={(e) => onReasonChange(e.target.value.slice(0, 100))}
              rows={2}
              maxLength={100}
            />
            <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, textAlign: "right", marginTop: "0.2rem" }}>
              {(reason ?? "").length}/100
            </Typography>
          </Box>
        )}
        <Box className={s.actions}>
          <button className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={onClose} disabled={loading}>
            {cancelLabel}
          </button>
          <button className={s.btnDanger} onClick={onConfirm} disabled={loading}>
            {loading ? confirmingLabel : confirmLabel}
          </button>
        </Box>
      </Box>
    </Dialog>
  );
}
